import { DeviceDetector } from './device-detector';
import { ScrollbarStyleDump } from './scrollbar-style-dump';

export class ScrollbarManager {
  constructor(private window: Window) {}

  private bodyStyles = this.window.document.body.style;
  private styleDump: ScrollbarStyleDump | null = null;
  private appsHiding: string[] = [];

  hideBodyScrollbars(app: string): void {
    if (this.appsHiding.length === 0 && this.styleDump === null) {
      this.saveStyleDump();
      this.hideScrollbars();
    }
    if (!this.appsHiding.includes(app)) {
      this.appsHiding.push(app);
    }
  }

  private saveStyleDump(): void {
    this.styleDump = {
      bodyOverflowX: this.bodyStyles.overflowX,
      bodyOverflowY: this.bodyStyles.overflowY,
      bodyPaddingBottom: this.bodyStyles.paddingBottom,
      bodyPaddingRight: this.bodyStyles.paddingRight,
      bodyHeight: this.bodyStyles.height,
      bodyMinWidth: this.bodyStyles.minWidth,
      bodyWidth: this.bodyStyles.width
    };
  }

  private hideScrollbars(): void {
    const effectiveBodyStyles = this.window.getComputedStyle(this.window.document.body);
    const scrollbarHeight = this.computePaddingBottom(effectiveBodyStyles);
    const scrollbarWidth = this.computePaddingRight(effectiveBodyStyles);
    const minimumBodyWidth = this.computeMinimumBodyWidth(effectiveBodyStyles);

    // Disable scroll bars
    this.bodyStyles.overflowX = 'hidden';
    this.bodyStyles.overflowY = 'hidden';

    // Set height to 100vh
    this.bodyStyles.height = '100vh';

    // Insert padding in place of scroll bars
    this.bodyStyles.paddingBottom = `${scrollbarHeight}px`;
    this.bodyStyles.paddingRight = `${scrollbarWidth}px`;

    // Ensure the body is at least as large as its content, so
    // that the padding is applied to the right of that content.
    // (If the body is scrollable horizontally, this normally
    // would not be the case.)
    this.bodyStyles.minWidth = `${minimumBodyWidth}px`;

    // Ensure that the body width is not set manually, e.g. to
    // '100%', as this would prevent the body width from being
    // reduced due to padding in content-box box-sizing mode.
    this.bodyStyles.width = 'initial';
  }

  private computePaddingBottom(effectiveBodyStyles: CSSStyleDeclaration): number {
    const documentElement = this.window.document.documentElement;
    const scrollbarHeight = this.window.innerHeight - documentElement.clientHeight;
    return parseFloat(effectiveBodyStyles.paddingBottom) + scrollbarHeight;
  }

  private computeMinimumBodyWidth(effectiveBodyStyles: CSSStyleDeclaration): number {
    const body = this.window.document.body;
    if (body.scrollWidth <= body.clientWidth) {
      return 0;
      // Workaround for bad computations on mobile devices, cf. MP-2484
    } else if (new DeviceDetector().isMobile()) {
      return 0;
    } else if (effectiveBodyStyles.boxSizing === 'border-box') {
      return body.scrollWidth + this.computePaddingRight(effectiveBodyStyles);
    } else {
      return body.scrollWidth;
    }
  }

  private computePaddingRight(effectiveBodyStyles: CSSStyleDeclaration): number {
    const documentElement = this.window.document.documentElement;
    const scrollbarWidth = this.window.innerWidth - documentElement.clientWidth;
    return parseFloat(effectiveBodyStyles.paddingRight) + scrollbarWidth;
  }

  resetBodyScrollbars(app: string): void {
    const appIndex = this.appsHiding.indexOf(app);
    if (appIndex > -1) {
      if (this.appsHiding.length === 1 && this.styleDump !== null) {
        this.applyStyleDump(this.styleDump);
        this.styleDump = null;
      }
      this.appsHiding.splice(appIndex, 1);
    }
  }

  private applyStyleDump(dump: ScrollbarStyleDump): void {
    // Reverting the overflow properties does not immediately
    // force a reflow to occur which would reevaluate - and
    // potentially increase - the maximum horizontal scroll
    // position. However this is necessary to prevent the
    // actual horizontal scroll position from being constrained
    // to a temporarily reduced maximum when reverting the
    // padding. Accessing the scroll position after changing
    // the overflow properties has the side effect to force
    // a reflow, and thus prevents inadvertant reductions of
    // the horizontal scroll position.
    //
    // https://gist.github.com/paulirish/5d52fb081b3570c81e3a

    this.bodyStyles.overflowX = dump.bodyOverflowX;
    this.bodyStyles.overflowY = dump.bodyOverflowY;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.window.document.documentElement.scrollLeft;

    this.bodyStyles.paddingBottom = dump.bodyPaddingBottom;
    this.bodyStyles.paddingRight = dump.bodyPaddingRight;
    this.bodyStyles.height = dump.bodyHeight;
    this.bodyStyles.minWidth = dump.bodyMinWidth;
    this.bodyStyles.width = dump.bodyWidth;
  }
}
